import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Target, Trophy, Calendar, Gift, Flame, Brain, 
  Activity, Heart, Dumbbell, Droplets, Beaker, Fish, 
  Sparkles, Star, Crown, Zap, Moon, Settings 
} from 'lucide-react';
import { RewardsOverview } from '../components/plan/RewardsOverview';
import { WeeklyChallenge } from '../components/plan/WeeklyChallenge';
import { GoalProgress } from '../components/plan/GoalProgress';
import { PlanSelector } from '../components/plan/PlanSelector';
import { MetricGraph } from '../components/plan/MetricGraph';
import { GoalCustomizer } from '../components/plan/GoalCustomizer';

const motivationalQuotes = [
  "Every completed goal releases dopamine, strengthening your motivation! 🧠",
  "You're rewiring your brain for success with each achievement! ⚡",
  "Your consistency is building powerful neural pathways! 🌟",
  "Feel that serotonin boost? That's your progress showing! 💫",
  "Your brain's reward system is lighting up with each win! 🎯"
];

const challenges = [
  {
    id: 1,
    title: 'Early Bird Champion',
    description: 'Complete 5 workouts before 8 AM',
    reward: 500,
    progress: 3,
    total: 5,
    message: "Morning workouts boost dopamine levels by 30%! 🌅"
  },
  {
    id: 2,
    title: 'Step Master',
    description: 'Hit 100k steps this week',
    reward: 1000,
    progress: 65000,
    total: 100000,
    message: "Walking activates your brain's reward pathways! 🚶‍♂️"
  }
];

const goals = [
  {
    icon: <Dumbbell className="w-4 h-4" />,
    name: 'Strength Training',
    target: 4,
    completed: 2,
    totalDays: 7,
    unit: 'sessions'
  },
  {
    icon: <Heart className="w-4 h-4" />,
    name: 'Cardio',
    target: 3,
    completed: 3,
    totalDays: 7,
    unit: 'sessions'
  },
  {
    icon: <Activity className="w-4 h-4" />,
    name: 'Daily Steps',
    target: 10000,
    completed: 8500,
    totalDays: 1,
    unit: 'steps'
  }
];

const behaviors = [
  {
    icon: <Moon className="w-4 h-4" />,
    name: 'Sleep 8+ Hours',
    target: 7,
    completed: 5,
    totalDays: 7,
    unit: 'nights'
  },
  {
    icon: <Droplets className="w-4 h-4" />,
    name: 'Hydration',
    target: 3,
    completed: 2,
    totalDays: 1,
    unit: 'liters'
  },
  {
    icon: <Brain className="w-4 h-4" />,
    name: 'Meditation',
    target: 5,
    completed: 4,
    totalDays: 7,
    unit: 'sessions'
  }
];

export function PlanPage() {
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [showGoalCustomizer, setShowGoalCustomizer] = useState(false);
  const [currentQuote] = useState(() => 
    motivationalQuotes[Math.floor(Math.random() * motivationalQuotes.length)]
  );

  // Sample data for graphs
  const weeklyData = {
    strain: [8.5, 12.3, 9.8, 14.2, 10.5, 13.1, 11.7],
    recovery: [82, 78, 85, 76, 88, 84, 87],
    sleep: [7.2, 6.8, 8.1, 7.5, 7.8, 8.2, 7.6],
    stress: [45, 52, 38, 48, 42, 35, 40]
  };

  return (
    <div className="space-y-4 sm:space-y-6 pb-20">
      <div className="mt-16 sm:mt-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative">
              <Calendar className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Your Journey</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">{currentQuote}</p>
            </div>
          </div>
          <button 
            onClick={() => setShowPlanSelector(true)}
            className="theme-button px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg flex items-center gap-2 relative group overflow-hidden text-sm sm:text-base"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary via-purple-500 to-primary opacity-0 group-hover:opacity-20 transition-opacity" />
            <Plus className="w-4 h-4" />
            <span className="relative">Change Plan</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
          {/* Rewards Overview */}
          <div className="theme-card p-4 sm:p-6 relative overflow-hidden backdrop-blur-xl border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <RewardsOverview />
            </div>
          </div>

          {/* Weekly Challenges */}
          <div className="theme-card p-4 sm:p-6 relative overflow-hidden backdrop-blur-xl border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold">Weekly Challenges</h2>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <span className="text-amber-500">4 days remaining</span>
                </div>
              </div>
              <div className="space-y-4">
                {challenges.map(challenge => (
                  <WeeklyChallenge key={challenge.id} challenge={challenge} />
                ))}
              </div>
            </div>
          </div>

          {/* Weekly Progress Graphs */}
          <div className="theme-card p-4 sm:p-6 relative overflow-hidden backdrop-blur-xl border-primary/20 md:col-span-2">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Activity className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold">Weekly Progress</h2>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium mb-2">Strain & Recovery</h3>
                  <div className="h-48">
                    <MetricGraph data={weeklyData.strain} color="blue" />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium mb-2">Sleep Quality</h3>
                  <div className="h-48">
                    <MetricGraph data={weeklyData.sleep} color="purple" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Goals */}
          <div className="theme-card p-4 sm:p-6 relative overflow-hidden backdrop-blur-xl border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold">Activity Goals</h2>
                </div>
                <button 
                  onClick={() => setShowGoalCustomizer(true)}
                  className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Customize</span>
                </button>
              </div>
              <div className="space-y-4">
                {goals.map((goal, index) => (
                  <GoalProgress key={index} {...goal} />
                ))}
              </div>
            </div>
          </div>

          {/* Behavior Goals */}
          <div className="theme-card p-4 sm:p-6 relative overflow-hidden backdrop-blur-xl border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent" />
            <div className="relative">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Brain className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold">Behavior Goals</h2>
                </div>
                <button 
                  onClick={() => setShowGoalCustomizer(true)}
                  className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Customize</span>
                </button>
              </div>
              <div className="space-y-4">
                {behaviors.map((behavior, index) => (
                  <GoalProgress key={index} {...behavior} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showPlanSelector && (
        <PlanSelector onClose={() => setShowPlanSelector(false)} />
      )}

      {showGoalCustomizer && (
        <GoalCustomizer 
          onClose={() => setShowGoalCustomizer(false)}
          currentGoals={[...goals, ...behaviors]}
        />
      )}
    </div>
  );
}